"use client";

import { useMemo, useState } from "react";
import { Headphones } from "lucide-react";
import ResourcePlayer from "@/components/resources/ResourcePlayer";
import ResourceThumbnail from "@/components/workspaces/admin/workspace/ResourceThumbnail";
import { useCoachPane } from "@/components/nav/NavCoachPane";
import MarkdownProse from "@/components/shared/MarkdownProse";
import { Eyebrow } from "@/components/ui/eyebrow";
import { ICON_TILE_TONES_ON_TINT } from "@/components/ui/icon-tile";
import { listMarkdownLinkTargets } from "@/lib/playbook/item-links";
import { getResourceHref } from "@/lib/agent-tools/content-links";
import { getResourceMediaKind, isPlayableMedia } from "@/lib/resources/media-kind";
import { buildResourceAnchorId } from "@/lib/resources/sections";
import { cn } from "@/lib/utils";

// Every row opens with a 16:9 frame, a video's poster or a recording's
// headphones, so the titles share one left edge.
const POSTER_CLASS = "aspect-video w-20 shrink-0 rounded-md";

/** The videos and recordings an answer links to, in the order it names them. */
function findLinkedMedia(content, resources) {
  const byHref = new Map(resources.flatMap((resource) => [
    [`#${buildResourceAnchorId(resource.id)}`, resource],
    [getResourceHref(resource.id), resource],
  ]));
  const linked = listMarkdownLinkTargets(content)
    .map((href) => byHref.get(href))
    .filter((resource) => resource && isPlayableMedia(resource));
  return [...new Map(linked.map((resource) => [resource.id, resource])).values()];
}

/**
 * A playbook answer in the home or playbook chat: links go straight to the
 * resource page or its row in the playbook; a player for each video or
 * recording it links to, so it can be watched without leaving the chat; and
 * the sections it drew on (SAL-3285).
 */
export default function PlaybookChatAnswer({ content, sources = [], resources = [] }) {
  const pane = useCoachPane();
  const media = useMemo(() => findLinkedMedia(content, resources), [content, resources]);

  // An in-page link has to show the page: a chat covering the screen (always,
  // on a phone) gets out of the way. Following the link the address bar
  // already shows changes no hash, so the playbook is told to go there again.
  const onLinkClick = (event) => {
    const href = event.target.closest?.("a")?.getAttribute("href");
    if (!href?.startsWith("#")) return;
    if (pane.isFullscreen) {
      if (pane.onFullscreenChange) pane.onFullscreenChange(false);
      else pane.onClose?.();
    }
    if (href === window.location.hash) {
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
  };

  return (
    <div className="space-y-3" onClick={onLinkClick}>
      <MarkdownProse
        content={content || ""}
        openLinksInNewTab
        className="prose-slate text-sm text-slate-700"
      />
      {media.length > 0 && (
        <ul className="space-y-0.5 border-t border-stone-200/70 pt-1.5">
          {media.map((resource) => (
            <li key={resource.id} className="-mx-1.5">
              <LinkedMediaRow resource={resource} />
            </li>
          ))}
        </ul>
      )}
      {sources.length > 0 && <AnswerSources sources={sources} />}
    </div>
  );
}

// A row on the chat itself, no card: it dips when pressed and the player
// fades in where it was (`press-dip`, `animate-reveal-in`, design-guidelines
// §14). The hover tint and the hairline above are the nav column's; the row
// is pulled out by its padding so the poster starts on the answer's edge.
function LinkedMediaRow({ resource }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const isVideo = getResourceMediaKind(resource) === "video";

  if (isPlaying) {
    return (
      <div className="animate-reveal-in space-y-2 p-1.5">
        <div className="overflow-hidden rounded-md">
          <ResourcePlayer resource={resource} />
        </div>
        <p className="truncate text-xs font-medium text-gray-900">{resource.label}</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      className="press-dip flex w-full items-center gap-3 rounded-lg p-1.5 text-left hover:bg-stone-200/50"
    >
      {isVideo ? (
        <ResourceThumbnail resource={resource} className={POSTER_CLASS} />
      ) : (
        <span
          aria-hidden="true"
          className={cn(POSTER_CLASS, "grid place-items-center", ICON_TILE_TONES_ON_TINT.neutral)}
        >
          <Headphones className="h-4 w-4" strokeWidth={1.75} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">
          {resource.label}
        </span>
        <span className="block text-xs text-gray-500">
          {isVideo ? "Watch here" : "Listen here"}
        </span>
      </span>
    </button>
  );
}

function AnswerSources({ sources }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs">
      <Eyebrow className="mb-2">Sources</Eyebrow>
      <ul className="space-y-1.5">
        {sources.map((source, index) => (
          <li key={`${source.sectionId || source.sectionTitle}-${index}`} className="space-y-0.5">
            {source.sectionId || source.url ? (
              <a
                href={source.url || `#${source.sectionId}`}
                {...(source.url && { target: "_blank", rel: "noopener noreferrer" })}
                className="font-medium text-action-600 hover:text-action-800 hover:underline"
              >
                {source.sectionTitle}
              </a>
            ) : (
              <span className="font-medium text-gray-900">{source.sectionTitle}</span>
            )}
            {source.quote && (
              <div className="border-l-2 border-gray-300 pl-2 italic text-gray-500">
                &ldquo;{source.quote}&rdquo;
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
